import { useState } from "react";
import { Link } from "react-router-dom";
import { Key, Trash2, Plus, Loader2, Calendar, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useMyApiKeys } from "@/hooks/use-queries";
import { api } from "@/lib/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { addDays, addYears } from "date-fns";
import { Copy, CheckCircle2 } from "lucide-react";

export default function ApiKeysOverview() {
  const { data: groups, isLoading } = useMyApiKeys();
  const queryClient = useQueryClient();

  const [createOpen, setCreateOpen] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const [keyName, setKeyName] = useState("");
  const [expirationOption, setExpirationOption] = useState("30d");
  const [newRawKey, setNewRawKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const createMutation = useMutation({
    mutationFn: async ({ agentId, name, expiresAt }: { agentId: string; name: string; expiresAt?: string }) => {
      const { data } = await api.post(`/agents/${agentId}/api-keys`, { name, expiresAt });
      return (data as any).data || data;
    },
    onSuccess: (data) => {
      setNewRawKey(data.rawKey);
      setKeyName("");
      queryClient.invalidateQueries({ queryKey: ["my-api-keys"] });
      toast.success("API key created");
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || "Failed to create API key");
    },
  });

  const revokeMutation = useMutation({
    mutationFn: async ({ agentId, keyId }: { agentId: string; keyId: string }) => {
      await api.post(`/agents/${agentId}/api-keys/${keyId}/revoke`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-api-keys"] });
      toast.success("API key revoked");
    },
    onError: () => toast.error("Failed to revoke API key"),
  });

  const handleCreate = () => {
    if (!selectedAgentId || !keyName.trim()) {
      toast.error("Please enter a key name");
      return;
    }
    const now = new Date();
    let expiresAt: string | undefined;
    if (expirationOption === "7d") expiresAt = addDays(now, 7).toISOString();
    else if (expirationOption === "30d") expiresAt = addDays(now, 30).toISOString();
    else if (expirationOption === "90d") expiresAt = addDays(now, 90).toISOString();
    else if (expirationOption === "1y") expiresAt = addYears(now, 1).toISOString();
    createMutation.mutate({ agentId: selectedAgentId, name: keyName.trim(), expiresAt });
  };

  const copyKey = async () => {
    if (newRawKey) {
      await navigator.clipboard.writeText(newRawKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const resetDialog = () => {
    setNewRawKey(null);
    setKeyName("");
    setExpirationOption("30d");
    setSelectedAgentId(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const totalKeys = groups?.reduce((sum: number, g: any) => sum + g.keys.length, 0) ?? 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-display">API Keys</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {totalKeys} key{totalKeys !== 1 ? "s" : ""} across {groups?.length ?? 0} agent{(groups?.length ?? 0) !== 1 ? "s" : ""}
          </p>
        </div>

        <Dialog open={createOpen} onOpenChange={(open) => { setCreateOpen(open); if (!open) resetDialog(); }}>
          <DialogTrigger asChild>
            <Button size="sm" disabled={!groups?.length}>
              <Plus className="size-4 mr-2" />
              New Key
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create API Key</DialogTitle>
              <DialogDescription>
                {newRawKey
                  ? "Copy this key now — it will not be shown again."
                  : "Select an agent and give the key a name."}
              </DialogDescription>
            </DialogHeader>

            {!newRawKey ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Agent</Label>
                  <Select onValueChange={setSelectedAgentId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select agent…" />
                    </SelectTrigger>
                    <SelectContent>
                      {groups?.map((g: any) => (
                        <SelectItem key={g.agentId} value={g.agentId}>
                          {g.agentName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Key Name</Label>
                  <Input
                    placeholder="e.g. production-v1"
                    value={keyName}
                    onChange={(e) => setKeyName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Expiration</Label>
                  <Select onValueChange={setExpirationOption} defaultValue={expirationOption}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select expiration" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7d">7 Days</SelectItem>
                      <SelectItem value="30d">30 Days</SelectItem>
                      <SelectItem value="90d">90 Days</SelectItem>
                      <SelectItem value="1y">1 Year</SelectItem>
                      <SelectItem value="never">Never</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter>
                  <Button onClick={handleCreate} disabled={createMutation.isPending} className="w-full">
                    {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Create API Key
                  </Button>
                </DialogFooter>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs bg-muted p-3 rounded-md break-all font-mono">
                    {newRawKey}
                  </code>
                  <Button variant="outline" size="icon" onClick={copyKey}>
                    {copied ? <CheckCircle2 className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-xs text-destructive font-medium">
                  This is the only time this key will be displayed. Store it securely.
                </p>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setCreateOpen(false)}>Done</Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {!groups?.length ? (
        <Card>
          <CardContent className="py-16 text-center text-muted-foreground">
            <Key className="mx-auto h-10 w-10 mb-3 opacity-40" />
            <p className="font-medium">No agents or API keys yet</p>
            <p className="text-sm mt-1">Register an agent and create your first key.</p>
            <Link to="/agents/new">
              <Button className="mt-4" size="sm">Register Agent</Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {groups.map((group: any) => (
            <div key={group.agentId}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <h2 className="font-semibold">{group.agentName}</h2>
                  <Badge variant="secondary" className="font-mono text-[10px]">
                    @{group.agentId}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedAgentId(group.agentId);
                      setCreateOpen(true);
                    }}
                  >
                    <Plus className="size-3.5 mr-1" />
                    New Key
                  </Button>
                  <Link to={`/agents/${group.agentId}/api-keys`}>
                    <Button variant="ghost" size="sm">
                      Manage
                      <ChevronRight className="size-3.5 ml-1" />
                    </Button>
                  </Link>
                </div>
              </div>

              {!group.keys.length ? (
                <Card>
                  <CardContent className="py-6 text-center text-muted-foreground text-sm">
                    No keys for this agent.
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-2">
                  {group.keys.map((key: any) => {
                    const isExpired = key.expiresAt && new Date(key.expiresAt) < new Date();
                    return (
                      <Card key={key.id} className={isExpired || key.revokedAt ? "opacity-60" : ""}>
                        <CardContent className="flex items-center justify-between py-3 px-4">
                          <div className="space-y-0.5">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">{key.name}</span>
                              {key.revokedAt ? (
                                <Badge variant="destructive" className="text-xs">Revoked</Badge>
                              ) : isExpired ? (
                                <Badge variant="destructive" className="text-xs">Expired</Badge>
                              ) : (
                                <Badge variant="secondary" className="text-xs">Active</Badge>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground font-mono">
                              <span>{key.tokenPrefix}...</span>
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {key.expiresAt
                                  ? `Expires ${new Date(key.expiresAt).toLocaleDateString()}`
                                  : "No expiry"}
                              </span>
                              {key.lastUsedAt && (
                                <span>Last used {new Date(key.lastUsedAt).toLocaleDateString()}</span>
                              )}
                            </div>
                          </div>
                          {!key.revokedAt && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => revokeMutation.mutate({ agentId: group.agentId, keyId: key.id })}
                              disabled={revokeMutation.isPending}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
